import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  ValidationErrors,
} from '@angular/forms';
import { Button, ButtonColor, ButtonSize } from 'app/store/models/button.model';
import { IUserBillingInfo, IUserSessionData } from 'app/store/models/user.model';
import { forkJoin, of, Subject } from 'rxjs';
import { SessionService } from 'app/store/services/session.service';
import { catchError, switchMap, takeUntil } from 'rxjs/operators';
import { ISelectItem } from 'app/store/models/select.model';
import { UserService } from 'app/store/services/user.service';
import { Store } from '@ngrx/store';
import { IReducers } from '../../store/reducers';
import { openSnackbar } from '../../store/actions/page.action';
import { getUserSession, setUserSessionData } from '../../store/actions/user.action';
import { DialogService } from '../../shared/ui/dialog/dialog.service';
import { LoadingSpinnerComponent } from '../../dialogs/loading-spinner/loading-spinner.component';
import { DiscountsService } from '../../store/services/discounts.service';
import { IPlanProduct } from 'app/store/models/discount.model';
import { Router } from '@angular/router';
import { environment } from 'environments/environment';
import { StripeEmbeddedService } from 'app/store/services/stripe-embedded.service';
import { getApiErrorMessage } from 'app/shared/http/api-error-message';

@Component({
  selector: 'app-billing-information',
  templateUrl: './billing-information.component.html',
  styleUrls: ['./billing-information.component.scss'],
})
export class BillingInformationComponent implements OnInit, OnDestroy {
  @ViewChild('peBillingHost', { static: false })
  peBillingHost: ElementRef<HTMLDivElement>;

  peBillingReady = false;
  private peBillingUnmount: (() => void) | null = null;
  private peBillingConfirm: (() => Promise<{ error?: unknown; setupIntent?: { id?: string } }>) | null =
    null;
  peBillingSubmitting = false;

  formGroup: FormGroup;

  cancelButtonProperties: Button = {
    text: 'Cancel',
    color: ButtonColor.RED,
    size: ButtonSize.NORMAL,
    route: ['/messages'],
    style: 'outline',
  };

  sessionData: IUserSessionData;

  billingInfo: IUserBillingInfo;

  accountId: number;

  formReady = false;

  showPlanSelection = false;
  plans: IPlanProduct[] = [];

  stripePaymentMethods: {
    default_payment_method_id: string | null;
    items: Array<{ id: string; is_default: boolean; summary: Record<string, unknown> }>;
  } | null = null;

  settingDefaultPm: string | null = null;

  /** True while first-time (no-sub) auto-load is fetching SetupIntent / mounting Stripe. */
  paymentElementAutoLoading = false;

  private destroy$: Subject<boolean> = new Subject<boolean>();

  constructor(
    private sessionService: SessionService,
    private userService: UserService,
    private readonly store$: Store<IReducers>,
    private readonly dialogService: DialogService,
    private readonly discountService: DiscountsService,
    private readonly router: Router,
    private readonly stripeEmbedded: StripeEmbeddedService,
    private readonly cdr: ChangeDetectorRef,
  ) {
  }

  /** Plan dropdown including empty placeholder (checkout flow only). */
  get planSelectItems(): ISelectItem[] {
    const placeholder: ISelectItem = { label: 'Select a plan…', value: '' };
    return [placeholder, ...this.plans.map(p => ({ label: p.name, value: p.plan_id }))];
  }

  private readonly validatePlanIdRequired = (control: AbstractControl): ValidationErrors | null =>
    this.normalizePlanId(control.value) === null ? { required: true } : null;

  /**
   * `app-select` / ng-select may store a primitive id or the full `{ label, value }` item.
   */
  private normalizePlanId(raw: unknown): number | null {
    if (raw === null || raw === undefined || raw === '') {
      return null;
    }
    if (typeof raw === 'object' && raw !== null && 'value' in raw) {
      const inner = (raw as { value: unknown }).value;
      if (inner === null || inner === undefined || inner === '') {
        return null;
      }
      if (typeof inner === 'number' && !Number.isNaN(inner)) {
        return inner;
      }
      const n = Number(inner);
      return Number.isNaN(n) ? null : n;
    }
    if (typeof raw === 'number' && !Number.isNaN(raw)) {
      return raw;
    }
    if (typeof raw === 'string') {
      const n = Number(raw);
      return raw === '' || Number.isNaN(n) ? null : n;
    }
    return null;
  }

  get impersonating(): boolean {
    return this.sessionService.isImpersonating();
  }

  get hasPaymentDiscount(): boolean {
    return this.sessionData?.has_payment_discount === true;
  }

  get discountFieldNote(): string {
    const name = this.billingInfo?.team_code_name?.trim();
    if (name) {
      return `You have applied team code ${name}.`;
    }
    return 'You have applied a team code to your subscription.';
  }

  /**
   * Existing subscription (plan on file) but not the “first checkout” flow: can apply a code without a new payment
   * method.
   */
  get canApplyDiscountOnly(): boolean {
    if (this.hasPaymentDiscount || this.showPlanSelection) {
      return false;
    }
    return !!this.sessionData?.plan_id;
  }

  applyingDiscountOnly = false;

  /** True while PUT /payment with use_existing_stripe_default_payment_method is in flight. */
  savingWithExistingPm = false;

  /**
   * Stripe already has a default payment method (card or e.g. bank). Used for first-time subscribe
   * without completing a new SetupIntent in this session.
   */
  get hasSavedPaymentMethod(): boolean {
    return !!(
      this.stripePaymentMethods?.default_payment_method_id ||
      (this.stripePaymentMethods?.items?.length ?? 0) > 0
    );
  }

  /** Label for the button that opens the Stripe Payment Element (checkout adds another; returning users replace). */
  get addPaymentMethodButtonLabel(): string {
    if (!this.showPlanSelection) {
      return 'Add or replace payment method';
    }
    return 'Add another payment method';
  }

  /**
   * Missing registration: subscribe using the default PaymentMethod already on the Stripe customer.
   */
  subscribeWithSavedPaymentMethod = () => {
    if (!this.showPlanSelection) {
      return;
    }
    const form = this.formGroup.getRawValue() as { plan_id?: unknown; team_code?: string };
    const planControl = this.formGroup.get('plan_id');
    planControl?.markAsTouched();
    planControl?.updateValueAndValidity();
    const planNum = this.normalizePlanId(form.plan_id);
    if (planNum === null) {
      this.store$.dispatch(
        openSnackbar({
          snackbarData: {
            data: { type: 'error', message: 'Choose a plan first.' },
          },
        }),
      );
      return;
    }
    const payload: Record<string, unknown> = {
      use_existing_stripe_default_payment_method: true,
      plan_id: planNum,
    };
    if (!this.hasPaymentDiscount && form.team_code) {
      payload.discount = String(form.team_code).trim();
    }
    this.savingWithExistingPm = true;
    const dialogRef = this.dialogService.openDialog({
      title: 'Starting subscription…',
      content: LoadingSpinnerComponent,
      canClose: false,
    });
    this.userService.updateAccountPayment(this.accountId, payload).subscribe(
      () => {
        this.savingWithExistingPm = false;
        dialogRef.close();
        this.refreshSessionThenNavigateToMessages();
      },
      error => {
        this.savingWithExistingPm = false;
        dialogRef.close();
        const msg = getApiErrorMessage(error, 'Update failed');
        this.store$.dispatch(
          openSnackbar({
            snackbarData: {
              data: { type: 'error', message: msg },
            },
          }),
        );
      },
    );
  };

  /**
   * Apply a team or discount code to an existing subscription (no new payment method in this request).
   */
  applyDiscountCodeOnly = () => {
    const code = (this.formGroup.get('team_code')?.value ?? '').toString().trim();
    if (!code) {
      this.store$.dispatch(
        openSnackbar({
          snackbarData: {
            data: { type: 'error', message: 'Enter a team or discount code first.' },
          },
        }),
      );
      return;
    }
    this.applyingDiscountOnly = true;
    this.userService.updateAccountPayment(this.accountId, { discount: code }).subscribe(
      () => {
        this.applyingDiscountOnly = false;
        this.formGroup.patchValue({ team_code: '' });
        this.store$.dispatch(getUserSession());
        this.userService
          .getBillingInfo(this.accountId)
          .subscribe(b => (this.billingInfo = b));
        this.store$.dispatch(
          openSnackbar({
            snackbarData: {
              data: { type: 'success', message: 'Discount or team code applied.' },
              verticalPosition: 'bottom',
            },
          }),
        );
      },
      err => {
        this.applyingDiscountOnly = false;
        const msg = getApiErrorMessage(err, 'Could not apply code');
        this.store$.dispatch(
          openSnackbar({
            snackbarData: { data: { type: 'error', message: msg } },
          }),
        );
      },
    );
  };

  private shouldShowPlanSelection(s: IUserSessionData): boolean {
    if (s.billing_status === 'Missing registration') {
      return true;
    }
    if (s.billing_status === 'Payment failed' && !s.plan_id) {
      return true;
    }
    return false;
  }

  /** Re-open the Payment Element (manual; never pass event into auto-preload). */
  openPaymentElementManually = () => this.startBillingPaymentElement(false);

  /**
   * @param autoPreload When true (user has no subscription yet), initial load: no blocking dialog and runs after page init.
   */
  startBillingPaymentElement = (autoPreload = false) => {
    this.peBillingUnmount?.();
    this.peBillingUnmount = null;
    this.peBillingConfirm = null;
    this.peBillingReady = false;
    if (autoPreload) {
      this.paymentElementAutoLoading = true;
    }
    const dialogRef = autoPreload
      ? null
      : this.dialogService.openDialog({
          title: 'Loading payment form…',
          content: LoadingSpinnerComponent,
          canClose: false,
        });
    this.userService.createSetupIntent(this.accountId).subscribe(
      async res => {
        dialogRef?.close();
        const cs = res?.client_secret;
        const pk =
          res?.stripe_publishable_key ||
          (environment as any).stripePublishableKey ||
          this.sessionData?.stripe_publishable_key;
        if (!cs || !pk) {
          this.store$.dispatch(
            openSnackbar({
              snackbarData: {
                data: {
                  type: 'error',
                  message: 'Could not start SetupIntent. Check STRIPE_PUBLISHABLE_KEY.',
                },
              },
            }),
          );
          return;
        }
        this.peBillingReady = true;
        this.cdr.detectChanges();
        setTimeout(() => void this.mountBillingPaymentElement(cs, String(pk)), 0);
      },
      err => {
        this.paymentElementAutoLoading = false;
        dialogRef?.close();
        this.store$.dispatch(
          openSnackbar({
            snackbarData: {
              data: {
                type: 'error',
                message: getApiErrorMessage(err, 'Failed to start payment form'),
              },
            },
          }),
        );
      },
    );
  };

  private async mountBillingPaymentElement(
    clientSecret: string,
    publishableKey: string,
    attempt = 0,
  ) {
    const el = this.peBillingHost?.nativeElement;
    if (!el) {
      if (attempt < 8) {
        this.cdr.detectChanges();
        setTimeout(
          () => void this.mountBillingPaymentElement(clientSecret, publishableKey, attempt + 1),
          0,
        );
        return;
      }
      this.store$.dispatch(
        openSnackbar({
          snackbarData: {
            data: {
              type: 'error',
              message: 'Could not show the payment form. Please try again.',
            },
          },
        }),
      );
      return;
    }
    this.peBillingUnmount?.();
    const mounted = await this.stripeEmbedded.mountPaymentElementForSetupIntent(
      clientSecret,
      el,
      publishableKey,
      () => {
        if (this.paymentElementAutoLoading) {
          this.paymentElementAutoLoading = false;
          this.cdr.detectChanges();
        }
      },
    );
    if (!mounted) {
      this.paymentElementAutoLoading = false;
      this.store$.dispatch(
        openSnackbar({
          snackbarData: {
            data: { type: 'error', message: 'Could not load Stripe.js.' },
          },
        }),
      );
      return;
    }
    // Safety fallback: if Stripe 'ready' never fires, stop showing spinner once mounted.
    if (this.paymentElementAutoLoading) {
      this.paymentElementAutoLoading = false;
      this.cdr.detectChanges();
    }
    this.peBillingUnmount = mounted.destroy;
    this.peBillingConfirm = mounted.confirm;
  }

  confirmBillingPaymentElement = async () => {
    if (this.peBillingSubmitting || !this.peBillingConfirm) {
      return;
    }
    if (this.showPlanSelection) {
      const planControl = this.formGroup.get('plan_id');
      planControl?.markAsTouched();
      planControl?.updateValueAndValidity();
      if (this.normalizePlanId(planControl?.value) === null) {
        this.store$.dispatch(
          openSnackbar({
            snackbarData: {
              data: { type: 'error', message: 'Choose a plan first.' },
            },
          }),
        );
        return;
      }
    }
    this.peBillingSubmitting = true;
    const out = await this.peBillingConfirm();
    this.peBillingSubmitting = false;
    if (out.error) {
      this.store$.dispatch(
        openSnackbar({
          snackbarData: {
            data: {
              type: 'error',
              message: (out.error as any)?.message || 'Payment failed',
            },
          },
        }),
      );
      return;
    }
    const setupIntentId = out.setupIntent?.id;
    if (!setupIntentId) {
      return;
    }
    const form = this.formGroup.getRawValue() as { plan_id?: unknown; team_code?: string };
    const payload: Record<string, unknown> = {
      setup_intent_id: setupIntentId,
    };
    const planNum = this.normalizePlanId(form.plan_id);
    if (this.showPlanSelection && planNum !== null) {
      payload.plan_id = planNum;
    }
    if (!this.hasPaymentDiscount && form.team_code) {
      payload.discount = form.team_code;
    }
    const dialogRef = this.dialogService.openDialog({
      title: 'Saving payment details...',
      content: LoadingSpinnerComponent,
      canClose: false,
    });
    this.userService.updateAccountPayment(this.accountId, payload).subscribe(
      () => {
        dialogRef.close();
        this.peBillingReady = false;
        this.peBillingUnmount?.();
        this.peBillingUnmount = null;
        this.peBillingConfirm = null;
        this.refreshSessionThenNavigateToMessages();
      },
      error => {
        dialogRef.close();
        const msg = getApiErrorMessage(error, 'Update failed');
        this.store$.dispatch(
          openSnackbar({
            snackbarData: {
              data: { type: 'error', message: msg },
            },
          }),
        );
      },
    );
  };

  setDefaultPaymentMethod = (pmId: string) => {
    this.settingDefaultPm = pmId;
    this.userService.setDefaultPaymentMethod(this.accountId, pmId).subscribe(
      () => {
        this.settingDefaultPm = null;
        this.store$.dispatch(getUserSession());
        this.reloadPaymentMethods();
        this.store$.dispatch(
          openSnackbar({
            snackbarData: {
              data: { type: 'success', message: 'Default payment method updated.' },
              verticalPosition: 'bottom',
            },
          }),
        );
      },
      err => {
        this.settingDefaultPm = null;
        this.store$.dispatch(
          openSnackbar({
            snackbarData: {
              data: {
                type: 'error',
                message: getApiErrorMessage(err, 'Could not update default.'),
              },
            },
          }),
        );
      },
    );
  };

  private reloadPaymentMethods() {
    this.userService
      .getPaymentMethods(this.accountId)
      .pipe(
        catchError(() =>
          of({
            default_payment_method_id: null,
            items: [] as Array<{
              id: string;
              is_default: boolean;
              summary: Record<string, unknown>;
            }>,
          }),
        ),
      )
      .subscribe(p => {
        this.stripePaymentMethods = p;
      });
  }

  formatPmLabel(item: { summary: Record<string, unknown> }): string {
    const s = item.summary;
    const t = (s.type as string) || '';
    if (t === 'card') {
      const brand = (s.brand as string) || 'Card';
      const last4 = (s.last4 as string) || '????';
      const em = s.exp_month;
      const ey = s.exp_year;
      const exp =
        em && ey ? ` · exp ${em}/${String(ey).slice(-2)}` : '';
      return `${brand} ·••• ${last4}${exp}`;
    }
    if (t === 'us_bank_account') {
      return `Bank ${(s.bank_name as string) || ''} ·••• ${(s.last4 as string) || ''}`.trim();
    }
    return t || 'Payment method';
  }

  private refreshSessionThenNavigateToMessages() {
    // Important: ensure billing_status is refreshed BEFORE we land on /messages
    // so the AuthenticatedComponent doesn’t open the billing dialog using stale session data.
    this.userService.getSessionData().subscribe(
      s => {
        if (s) {
          this.store$.dispatch(setUserSessionData({ data: s }));
        } else {
          this.store$.dispatch(getUserSession());
        }
        this.store$.dispatch(
          openSnackbar({
            snackbarData: {
              data: { type: 'success', message: 'Information was successfully updated' },
              verticalPosition: 'bottom',
            },
          }),
        );
        this.router.navigate(['/messages']);
      },
      () => {
        // Fallback: still navigate, but try to refresh in background.
        this.store$.dispatch(getUserSession());
        this.store$.dispatch(
          openSnackbar({
            snackbarData: {
              data: { type: 'success', message: 'Information was successfully updated' },
              verticalPosition: 'bottom',
            },
          }),
        );
        this.router.navigate(['/messages']);
      },
    );
  }

  ngOnInit() {
    this.sessionService
      .onReady()
      .pipe(
        takeUntil(this.destroy$),
        switchMap(s => {
          this.sessionData = s;
          this.accountId = s.id;
          const needPlans = this.shouldShowPlanSelection(s);
          this.showPlanSelection = needPlans;
          return forkJoin({
            billing: this.userService.getBillingInfo(this.accountId),
            sessionFresh: this.userService.getSessionData().pipe(
              catchError(() => of(s)),
            ),
            paymentMethods: this.userService.getPaymentMethods(this.accountId).pipe(
              catchError(() =>
                of({
                  default_payment_method_id: null,
                  items: [] as Array<{
                    id: string;
                    is_default: boolean;
                    summary: Record<string, unknown>;
                  }>,
                }),
              ),
            ),
            planProducts: needPlans
              ? this.discountService.getPlanProducts().pipe(
                  catchError(() => of({ items: [] as IPlanProduct[] })),
                )
              : of({ items: [] as IPlanProduct[] }),
          });
        }),
      )
      .subscribe(
        ({ billing, sessionFresh, paymentMethods, planProducts }) => {
          this.billingInfo = billing;
          this.sessionData = { ...this.sessionData, ...sessionFresh };
          this.stripePaymentMethods = paymentMethods;
          if (planProducts?.items?.length) {
            this.plans = planProducts.items;
          }
          const planValidators = this.showPlanSelection ? [this.validatePlanIdRequired] : [];
          this.formGroup = new FormGroup({
            team_code: new FormControl(''),
            plan_id: new FormControl({ value: '', disabled: false }, planValidators),
          });
          this.formReady = true;
          if (this.showPlanSelection && !this.hasSavedPaymentMethod) {
            this.paymentElementAutoLoading = true;
            this.cdr.detectChanges();
            setTimeout(() => this.startBillingPaymentElement(true), 0);
          }
        },
        () => {
          this.formReady = true;
        },
      );
  }

  ngOnDestroy() {
    this.peBillingUnmount?.();
    this.destroy$.next(false);
  }
}
